/**
 * Dashboard widget configurations per role
 * Simple but effective dashboards tailored to each role's responsibilities
 */

import { ROLE_LABELS, getDataScope } from './Roles.js';
import { permissions } from '../services/PermissionService.js';

/**
 * Widget definitions - simple and focused
 */
export const WIDGETS = {
    // Financial Overview
    COMPANY_REVENUE: 'company_revenue',
    REVENUE_TREND: 'revenue_trend',
    PIPELINE_VALUE: 'pipeline_value',
    FORECAST: 'forecast',
    
    // Sales Performance
    TOP_PERFORMERS: 'top_performers',
    CONVERSION_RATE: 'conversion_rate',
    AVERAGE_DEAL_SIZE: 'average_deal_size',
    WON_VS_LOST: 'won_vs_lost',
    
    // Team & Department
    TEAM_PERFORMANCE: 'team_performance',
    DEPARTMENT_PERFORMANCE: 'department_performance',
    TEAM_RANKING: 'team_ranking',
    
    // Personal Productivity
    MY_DEALS: 'my_deals',
    MY_ACCOUNTS: 'my_accounts',
    MY_CONTACTS: 'my_contacts',
    MY_REVENUE: 'my_revenue',
    REVENUE_TARGET: 'revenue_target',
    
    // Activity & Tasks
    TODAYS_TASKS: 'todays_tasks',
    UPCOMING_MEETINGS: 'upcoming_meetings',
    RECENT_ACTIVITIES: 'recent_activities',
    PENDING_FOLLOW_UPS: 'pending_follow_ups',
    TODAYS_CALLS: 'todays_calls',
    
    // Pipeline & Deals
    OPEN_DEALS: 'open_deals',
    DEALS_CLOSING_SOON: 'deals_closing_soon',
    DEALS_REQUIRING_ATTENTION: 'deals_requiring_attention',
    CLOSED_WON: 'closed_won',
    QUALIFIED_OPPORTUNITIES: 'qualified_opportunities',
    
    // Customer Success
    ASSIGNED_CUSTOMERS: 'assigned_customers',
    RENEWALS: 'renewals',
    CUSTOMER_HEALTH: 'customer_health',
    RECENT_INTERACTIONS: 'recent_interactions',
    
    // Business Development
    NEW_ACCOUNTS: 'new_accounts',
    LEAD_CONVERSION: 'lead_conversion',
    BUSINESS_PIPELINE: 'business_pipeline',
    NEW_CUSTOMERS: 'new_customers',
    
    // Overview
    CRM_SUMMARY: 'crm_summary',
    PIPELINE_OVERVIEW: 'pipeline_overview',
    RECENT_DEALS: 'recent_deals',
    RECENT_ACCOUNTS: 'recent_accounts',
    USER_ACTIVITY: 'user_activity',
    
    // Business Health
    REVENUE_GROWTH: 'revenue_growth',
    MONTHLY_GROWTH: 'monthly_growth',
    MAJOR_OPPORTUNITIES: 'major_opportunities',
    BUSINESS_HEALTH: 'business_health',
    SALES_PERFORMANCE: 'sales_performance',
    TOP_CUSTOMERS: 'top_customers',
    AVG_DAYS_IN_STAGE: 'avg_days_in_stage',
    WIN_RATE_BY_SOURCE: 'win_rate_by_source',
    LOST_BY_CATEGORY: 'lost_by_category'
};

export const FINANCIAL_WIDGETS = Object.freeze([
    WIDGETS.COMPANY_REVENUE,
    WIDGETS.REVENUE_TREND,
    WIDGETS.PIPELINE_VALUE,
    WIDGETS.FORECAST,
    WIDGETS.AVERAGE_DEAL_SIZE,
    WIDGETS.MY_REVENUE,
    WIDGETS.REVENUE_TARGET,
    WIDGETS.REVENUE_GROWTH,
    WIDGETS.MONTHLY_GROWTH,
    WIDGETS.SALES_PERFORMANCE,
    WIDGETS.TOP_CUSTOMERS,
    WIDGETS.TOP_PERFORMERS,
    WIDGETS.TEAM_PERFORMANCE,
    WIDGETS.TEAM_RANKING,
]);

/**
 * Dashboard configurations per role
 * Each role gets 4-6 focused widgets
 * Simplified to match 11 core roles
 */
export const ROLE_DASHBOARDS = {
    super_admin: {
        title: 'Company Overview',
        subtitle: 'Complete business intelligence',
        widgets: [
            WIDGETS.COMPANY_REVENUE,
            WIDGETS.PIPELINE_VALUE,
            WIDGETS.REVENUE_TREND,
            WIDGETS.TOP_PERFORMERS,
            WIDGETS.TEAM_PERFORMANCE,
            WIDGETS.RECENT_ACTIVITIES,
            WIDGETS.AVG_DAYS_IN_STAGE,
            WIDGETS.WIN_RATE_BY_SOURCE,
            WIDGETS.LOST_BY_CATEGORY
        ]
    },
    
    admin: {
        title: 'CRM Administration',
        subtitle: 'System overview and management',
        widgets: [
            WIDGETS.CRM_SUMMARY,
            WIDGETS.PIPELINE_OVERVIEW,
            WIDGETS.RECENT_DEALS,
            WIDGETS.USER_ACTIVITY,
            WIDGETS.RECENT_ACCOUNTS
        ]
    },
    
    ceo: {
        title: 'Executive Dashboard',
        subtitle: 'Business KPIs and insights',
        widgets: [
            WIDGETS.COMPANY_REVENUE,
            WIDGETS.REVENUE_GROWTH,
            WIDGETS.PIPELINE_VALUE,
            WIDGETS.FORECAST,
            WIDGETS.SALES_PERFORMANCE,
            WIDGETS.TOP_CUSTOMERS,
            WIDGETS.AVG_DAYS_IN_STAGE,
            WIDGETS.WIN_RATE_BY_SOURCE,
            WIDGETS.LOST_BY_CATEGORY
        ]
    },
    
    sales_manager: {
        title: 'My Team Dashboard',
        subtitle: 'Team performance and deals',
        widgets: [
            WIDGETS.TEAM_PERFORMANCE,
            WIDGETS.OPEN_DEALS,
            WIDGETS.CLOSED_WON,
            WIDGETS.DEALS_REQUIRING_ATTENTION,
            WIDGETS.TEAM_RANKING,
            WIDGETS.RECENT_ACTIVITIES
        ]
    },
    
    sales_executive: {
        title: 'My Performance',
        subtitle: 'Personal deals and activities',
        widgets: [
            WIDGETS.MY_DEALS,
            WIDGETS.MY_ACCOUNTS,
            WIDGETS.REVENUE_TARGET,
            WIDGETS.DEALS_CLOSING_SOON,
            WIDGETS.TODAYS_TASKS,
            WIDGETS.UPCOMING_MEETINGS
        ]
    },
    
    account_manager: {
        title: 'Account Manager',
        subtitle: 'Overview',
        widgets: [
            WIDGETS.RECENT_ACTIVITIES
        ]
    },

    delivery_manager: {
        title: 'Delivery Manager',
        subtitle: 'Overview',
        widgets: [
            WIDGETS.RECENT_ACTIVITIES
        ]
    },

    finance: {
        title: 'Finance',
        subtitle: 'Overview',
        widgets: [
            WIDGETS.RECENT_ACTIVITIES
        ]
    },

    crm_administrator: {
        title: 'CRM Administrator',
        subtitle: 'System overview',
        widgets: [
            WIDGETS.CRM_SUMMARY,
            WIDGETS.RECENT_ACTIVITIES
        ]
    },

    viewer: {
        title: 'Overview',
        subtitle: 'Read-only business overview',
        widgets: [
            WIDGETS.PIPELINE_OVERVIEW,
            WIDGETS.RECENT_DEALS,
            WIDGETS.CLOSED_WON
        ]
    }
};

/**
 * Get dashboard config for current user's role
 */
export function getDashboardConfig(role) {
    const normalizedRole = role || 'viewer';
    return ROLE_DASHBOARDS[normalizedRole] || ROLE_DASHBOARDS.viewer;
}

/**
 * Get widget metadata for rendering
 */
export function getWidgetConfig(widgetId) {
    const configs = {
        [WIDGETS.COMPANY_REVENUE]: {
            title: 'Company Revenue',
            type: 'kpi',
            format: 'currency',
            icon: '💰'
        },
        [WIDGETS.REVENUE_TREND]: {
            title: 'Revenue Trend',
            type: 'chart',
            chartType: 'line',
            icon: '📈'
        },
        [WIDGETS.PIPELINE_VALUE]: {
            title: 'Pipeline Value',
            type: 'kpi',
            format: 'currency',
            icon: '🎯'
        },
        [WIDGETS.FORECAST]: {
            title: 'Forecast',
            type: 'kpi',
            format: 'currency',
            icon: '🔮'
        },
        [WIDGETS.TOP_PERFORMERS]: {
            title: 'Top Performers',
            type: 'list',
            icon: '⭐'
        },
        [WIDGETS.CONVERSION_RATE]: {
            title: 'Conversion Rate',
            type: 'kpi',
            format: 'percentage',
            icon: '📊'
        },
        [WIDGETS.AVERAGE_DEAL_SIZE]: {
            title: 'Average Deal Size',
            type: 'kpi',
            format: 'currency',
            icon: '💵'
        },
        [WIDGETS.WON_VS_LOST]: {
            title: 'Won vs Lost',
            type: 'chart',
            chartType: 'pie',
            icon: '🏆'
        },
        [WIDGETS.TEAM_PERFORMANCE]: {
            title: 'Team Performance',
            type: 'list',
            icon: '👥'
        },
        [WIDGETS.DEPARTMENT_PERFORMANCE]: {
            title: 'Department Performance',
            type: 'list',
            icon: '🏢'
        },
        [WIDGETS.TEAM_RANKING]: {
            title: 'Team Ranking',
            type: 'list',
            icon: '📊'
        },
        [WIDGETS.MY_DEALS]: {
            title: 'My Deals',
            type: 'list',
            icon: '💼'
        },
        [WIDGETS.MY_ACCOUNTS]: {
            title: 'My Accounts',
            type: 'list',
            icon: '🏢'
        },
        [WIDGETS.MY_CONTACTS]: {
            title: 'My Contacts',
            type: 'list',
            icon: '👤'
        },
        [WIDGETS.MY_REVENUE]: {
            title: 'My Revenue',
            type: 'kpi',
            format: 'currency',
            icon: '💰'
        },
        [WIDGETS.REVENUE_TARGET]: {
            title: 'Revenue Target',
            type: 'progress',
            icon: '🎯'
        },
        [WIDGETS.TODAYS_TASKS]: {
            title: "Today's Tasks",
            type: 'list',
            icon: '✅'
        },
        [WIDGETS.UPCOMING_MEETINGS]: {
            title: 'Upcoming Meetings',
            type: 'list',
            icon: '📅'
        },
        [WIDGETS.RECENT_ACTIVITIES]: {
            title: 'Recent Activities',
            type: 'list',
            icon: '📋'
        },
        [WIDGETS.PENDING_FOLLOW_UPS]: {
            title: 'Pending Follow-ups',
            type: 'list',
            icon: '📞'
        },
        [WIDGETS.TODAYS_CALLS]: {
            title: "Today's Calls",
            type: 'list',
            icon: '📱'
        },
        [WIDGETS.OPEN_DEALS]: {
            title: 'Open Deals',
            type: 'list',
            icon: '🔓'
        },
        [WIDGETS.DEALS_CLOSING_SOON]: {
            title: 'Deals Closing Soon',
            type: 'list',
            icon: '⏰'
        },
        [WIDGETS.DEALS_REQUIRING_ATTENTION]: {
            title: 'Deals Requiring Attention',
            type: 'list',
            icon: '⚠️'
        },
        [WIDGETS.CLOSED_WON]: {
            title: 'Closed Won',
            type: 'kpi',
            format: 'currency',
            icon: '🏆'
        },
        [WIDGETS.QUALIFIED_OPPORTUNITIES]: {
            title: 'Qualified Opportunities',
            type: 'list',
            icon: '💎'
        },
        [WIDGETS.ASSIGNED_CUSTOMERS]: {
            title: 'Assigned Customers',
            type: 'list',
            icon: '🤝'
        },
        [WIDGETS.RENEWALS]: {
            title: 'Renewals',
            type: 'list',
            icon: '🔄'
        },
        [WIDGETS.CUSTOMER_HEALTH]: {
            title: 'Customer Health',
            type: 'list',
            icon: '💚'
        },
        [WIDGETS.RECENT_INTERACTIONS]: {
            title: 'Recent Interactions',
            type: 'list',
            icon: '💬'
        },
        [WIDGETS.NEW_ACCOUNTS]: {
            title: 'New Accounts',
            type: 'list',
            icon: '🆕'
        },
        [WIDGETS.LEAD_CONVERSION]: {
            title: 'Lead Conversion',
            type: 'kpi',
            format: 'percentage',
            icon: '📈'
        },
        [WIDGETS.BUSINESS_PIPELINE]: {
            title: 'Business Pipeline',
            type: 'list',
            icon: '🎯'
        },
        [WIDGETS.NEW_CUSTOMERS]: {
            title: 'New Customers',
            type: 'list',
            icon: '🎉'
        },
        [WIDGETS.CRM_SUMMARY]: {
            title: 'CRM Summary',
            type: 'summary',
            icon: '📊'
        },
        [WIDGETS.PIPELINE_OVERVIEW]: {
            title: 'Pipeline Overview',
            type: 'summary',
            icon: '🎯'
        },
        [WIDGETS.RECENT_DEALS]: {
            title: 'Recent Deals',
            type: 'list',
            icon: '💼'
        },
        [WIDGETS.RECENT_ACCOUNTS]: {
            title: 'Recent Accounts',
            type: 'list',
            icon: '🏢'
        },
        [WIDGETS.USER_ACTIVITY]: {
            title: 'User Activity',
            type: 'list',
            icon: '👤'
        },
        [WIDGETS.REVENUE_GROWTH]: {
            title: 'Revenue Growth',
            type: 'chart',
            chartType: 'line',
            icon: '📈'
        },
        [WIDGETS.MONTHLY_GROWTH]: {
            title: 'Monthly Growth',
            type: 'chart',
            chartType: 'bar',
            icon: '📊'
        },
        [WIDGETS.MAJOR_OPPORTUNITIES]: {
            title: 'Major Opportunities',
            type: 'list',
            icon: '💎'
        },
        [WIDGETS.BUSINESS_HEALTH]: {
            title: 'Business Health',
            type: 'summary',
            icon: '💚'
        },
        [WIDGETS.SALES_PERFORMANCE]: {
            title: 'Sales Performance',
            type: 'summary',
            icon: '📈'
        },
        [WIDGETS.TOP_CUSTOMERS]: {
            title: 'Top Customers',
            type: 'list',
            icon: '⭐'
        },
        [WIDGETS.AVG_DAYS_IN_STAGE]: {
            title: 'Avg Days in Stage',
            type: 'chart',
            chartType: 'bar',
            icon: '⏱️'
        },
        [WIDGETS.WIN_RATE_BY_SOURCE]: {
            title: 'Win Rate by Source',
            type: 'list',
            icon: '📊'
        },
        [WIDGETS.LOST_BY_CATEGORY]: {
            title: 'Lost by Category',
            type: 'chart',
            chartType: 'pie',
            icon: '📉'
        }
    };
    
    return configs[widgetId] || { title: widgetId, type: 'unknown', icon: '📊' };
}

/**
 * Widget required permission mapping.
 * Dashboard widgets are filtered at render time using PermissionService.can(...)
 */
export const WIDGET_REQUIRED_PERMISSION = {
    // Strategic/company oversight
    [WIDGETS.COMPANY_REVENUE]: 'dashboard:company',
    [WIDGETS.REVENUE_TREND]: 'dashboard:company',
    [WIDGETS.PIPELINE_VALUE]: 'dashboard:company',
    [WIDGETS.FORECAST]: 'dashboard:company',
    [WIDGETS.REVENUE_GROWTH]: 'dashboard:company',
    [WIDGETS.MONTHLY_GROWTH]: 'dashboard:company',
    [WIDGETS.SALES_PERFORMANCE]: 'dashboard:company',
    [WIDGETS.TOP_CUSTOMERS]: 'dashboard:company',

    // Team oversight / execution monitoring
    [WIDGETS.TEAM_PERFORMANCE]: 'dashboard:team',
    [WIDGETS.DEPARTMENT_PERFORMANCE]: 'dashboard:company',
    [WIDGETS.TEAM_RANKING]: 'dashboard:team',
    [WIDGETS.CONVERSION_RATE]: 'dashboard:team',
    [WIDGETS.DEALS_CLOSING_SOON]: 'dashboard:team',
    [WIDGETS.DEALS_REQUIRING_ATTENTION]: 'dashboard:team',
    [WIDGETS.OPEN_DEALS]: 'dashboard:team',

    // Personal/own execution
    [WIDGETS.MY_DEALS]: 'dashboard:own',
    [WIDGETS.MY_ACCOUNTS]: 'dashboard:own',
    [WIDGETS.MY_CONTACTS]: 'dashboard:own',
    [WIDGETS.MY_REVENUE]: 'dashboard:own',
    [WIDGETS.REVENUE_TARGET]: 'dashboard:own',
    [WIDGETS.TODAYS_TASKS]: 'dashboard:own',
    [WIDGETS.UPCOMING_MEETINGS]: 'dashboard:own',
    [WIDGETS.RECENT_ACTIVITIES]: 'dashboard:own',
    [WIDGETS.PENDING_FOLLOW_UPS]: 'dashboard:own',
    [WIDGETS.TODAYS_CALLS]: 'dashboard:own',
    [WIDGETS.QUALIFIED_OPPORTUNITIES]: 'dashboard:own',

    // BD/qualification (treat as own)
    [WIDGETS.BUSINESS_PIPELINE]: 'dashboard:own',
    [WIDGETS.NEW_ACCOUNTS]: 'dashboard:own',
    [WIDGETS.LEAD_CONVERSION]: 'dashboard:own',
    [WIDGETS.NEW_CUSTOMERS]: 'dashboard:own',

    // Customer success (assigned)
    [WIDGETS.ASSIGNED_CUSTOMERS]: 'dashboard:assigned',
    [WIDGETS.RENEWALS]: 'dashboard:assigned',
    [WIDGETS.CUSTOMER_HEALTH]: 'dashboard:assigned',
    [WIDGETS.RECENT_INTERACTIONS]: 'dashboard:assigned',

    // “General overview” widgets that should still respect viewer/data scope
    [WIDGETS.CRM_SUMMARY]: 'dashboard:company',
    [WIDGETS.PIPELINE_OVERVIEW]: 'dashboard:company',
    [WIDGETS.RECENT_DEALS]: 'dashboard:company',
    [WIDGETS.RECENT_ACCOUNTS]: 'dashboard:company',
    [WIDGETS.USER_ACTIVITY]: 'dashboard:company',

    // Fallbacks (if a widget id isn’t listed above, it won’t be filtered)
};

// Backwards-compatible entry point
export function canViewWidget(widgetId, role) {
    return true;
}
